import { useCallback, useContext, useEffect } from 'react'
import { Outlet as ReactOutlet, useNavigate, useParams } from 'react-router'

import { useMutation, useQuery } from '@apollo/client'

import { CHG } from 'tools/Input3'
import { Loading } from 'tools/Loading'
import notifier, { readableError } from 'tools/Notify'
import { freeze } from 'utils/dom'

import { useNav } from 'app/Nav/Tools'
import { GlobalContext } from 'reducer/global'

const defaultFrame = {
  Context: undefined, // <Context> func [required] —
  Provider: undefined, // <Provider> func
  table: '', // for the sys data calls
  actions: {
    LOAD: undefined, // reducer action to use for setting
    RESET: undefined, // reducer action for resetting state
    DELETE: undefined, // reducer action for deleting from state
    SAVER: undefined // reducer action for remembering the stateful onSave function
  },
  change: {
    mutation: undefined,
    vars: {}, // any args to always include on every mutate
    queryName: '', // name of the query in results
    refetchQueries: undefined, // refetchQueries ["name"] or full w/vars
    makeRefetch: undefined // to generate the refetchQueries if it is more involved
  },
  list: {
    query: undefined, // listing things vs loading one thing
    fetchPolicy: undefined
  },
  load: {
    query: undefined,
    addVars: {},
    fetchPolicy: '', // additonal args for upsert
    queryName: '', // name of the query in results
    wait: false,
    failOk: false,
    pathKey: 'targetId' // the id element in the path
  },
  debug: false // 'name' // if truthy, this is the label for debugging
}

function getPolicy({ fetchPolicy }) {
  return { fetchPolicy: fetchPolicy || 'cache-and-network' }
}

// perhaps integrate this with the rest of <Editable ..> stack?
export function useListData(frame, variables, extra) {
  const [, notify] = useContext(GlobalContext)
  const [state, dispatch] = useContext(frame.Context)

  const { loading } = useQuery(frame.list.query, {
    fetchPolicy: frame.list.fetchPolicy,
    variables,
    onCompleted(result) {
      const value = result[Object.keys(result)[0]]
      if (value.status.success) {
        dispatch({
          type: frame.actions.RESET || 'unknown frame action for RESET',
          value,
          extra
        })
      } else {
        console.error('Error listing data', value)
      }
    },
    onError(err) {
      notifier.error(notify, readableError(err))
    }
  })

  return [state, dispatch, loading]
}

export function makeFrame(frame) {
  frame = { ...defaultFrame, ...frame }

  frame.actions = freeze({ ...defaultFrame.actions, ...frame.actions })
  frame.change = freeze({ ...defaultFrame.change, ...frame.change })
  frame.list = freeze({
    ...defaultFrame.list,
    ...frame.list,
    ...getPolicy(frame.list)
  })
  frame.load = freeze({
    ...defaultFrame.load,
    ...frame.load,
    ...getPolicy(frame.load)
  })
  if (frame.debug) {
    frame.DEBUG = (what, data) =>
      console.log(`<${what} ${frame.debug}>`, { ...data, frame })
  } else {
    frame.DEBUG = () => {}
  }
  return freeze(frame)
}

////////////////////////////////////////////////////////////////////////////////
function doDispatch({ dispatch, action: type, frame, args = {}, value }) {
  type ??= frame.actions.LOAD
  if (!type) {
    console.error('doDispatch', { type, args, value })
    throw new Error('NO HANDLER FOR DISPATCH TYPE!')
  } else {
    if (value?.result) {
      value = value.result
    } else if (value?.results) {
      value = value.results
    }
    if (Array.isArray(value)) {
      value = value[0]
    }
    value = { ...value, _loaded: true }
    dispatch({ type, value, ...args })
  }
}

function getQueryName({ queryName }, data) {
  if (!queryName) return Object.keys(data)[0]
  return queryName
}

export function useLoader(frame, targetId) {
  const [, notify] = useContext(GlobalContext)
  // const [{ user }] = useContext(GlobalContext)
  const [state, dispatch] = useContext(frame.Context)
  const params = useParams()
  targetId ??= params[frame.load.pathKey]
  const variables = { ...frame.load.addVars, id: targetId }
  const skip = !targetId || targetId === '_'
  frame.DEBUG('Loader', { skip, variables, targetId })

  const { data, loading, error } = useQuery(frame.load.query, {
    variables,
    skip,
    fetchPolicy: frame.fetchPolicy
  })

  useEffect(() => {
    if (!skip) {
      if (error) {
        console.error('Loader', error)
        notifier.error(notify, error)
      } else if (!loading && data) {
        frame.DEBUG('Loader Complete', { result: { ...data }, targetId })
        const queryName = getQueryName(frame.load, data)
        const result = data[queryName]
        if (!getSuccess(result)) {
          if (frame.load.failOk) {
            doDispatch({
              dispatch,
              frame,
              value: { result: {} },
              args: { fail: true }
            })
          } else {
            notifier.error(notify, getMessage(result))
          }
        } else {
          doDispatch({ dispatch, frame, value: result, args: { load: true } })
        }
      } else {
        // console.error('no result?')
        frame.DEBUG('Loader still Loading...')
      }
    }
  }, [dispatch, frame, loading, notify, targetId, data, error, skip])

  return { loading, state, targetId, skip }
}

function getSuccess(result) {
  return result.status ? result.status.success : result.success
}

function getMessage(result) {
  return result.status
    ? result.status.message
    : result.message || result.reason || 'Unexpected result'
}

////////////////////////////////////////////////////////////////////////////////
export function useSaver(frame) {
  const [, notify] = useContext(GlobalContext)
  const { Context, change, actions } = frame
  const [state, dispatch] = useContext(Context)
  const [{ user }] = useContext(GlobalContext)
  const [changer] = useMutation(change.mutation)
  const navigate = useNavigate()
  const id = state.id
  const shortId = getShorterId(state)
  const onSave = useCallback(
    (props, value, good, bad) => {
      const {
        component: _component,
        table,
        dirty = true,
        makeVars = frame.change.makeVars,
        cmeta = {},
        meta = {},
        followId,
        ...args
      } = props
      const field = props.field ?? props.token
      const component = _component ?? table
      frame.DEBUG('Save:onSave', props)
      if (!dirty) {
        good && good()
      } else {
        let vars = frame.change.vars
        let kvalue = {}
        if (field) kvalue = { [field]: value }

        if (component) {
          vars = {
            ...vars,
            [component]: { id, ...kvalue, ...cmeta },
            ...meta
          }
        } else {
          vars = { ...vars, id, ...kvalue, ...meta }
        }
        if (makeVars) {
          vars = makeVars({
            vars,
            component,
            token: field,
            field,
            value,
            frame,
            args
          })
        }
        // todo: search for componentId use

        let refetchQueries = props.refetchQueries || frame.change.refetchQueries
        // or overrides
        const makeRefetch = frame.change.makeRefetch || props.makeRefetch
        if (makeRefetch) {
          refetchQueries = makeRefetch(frame, vars)
        }

        let { queryName } = change
        frame.DEBUG('Save:submit', { queryName, variables: vars, refetchQueries })

        changer({
          variables: vars,
          refetchQueries,
          onCompleted(data) {
            // this is two levels deep and doesn't always update
            // if there are changes, like to 'shortId' — need to refactor
            const queryName = getQueryName(change, data)
            const result = data[queryName]
            frame.DEBUG('Save:received', { queryName, data, result })

            if (getSuccess(result) === false) {
              const msg = getMessage(result)
              if (bad) bad(msg)
              else notifier.error(notify, msg)
              frame.DEBUG('Save:failed', { result })
              return
            }

            let value = result.result
            if (value === undefined && component && result[component]) {
              value = result[component]
            }

            // optional
            const action = result.deleted ? actions.DELETE : actions.LOAD
            if (action)
              doDispatch({
                dispatch,
                action,
                args: { mutation: true, user, component },
                value,
                frame
              })

            if (followId) {
              const newId = getShorterId(value)
              if (!newId) {
                console.error('Unable to find new Id to redirect after create')
                // this only works if using <Input3 />
              } else if (newId !== shortId) {
                frame.DEBUG('Save->followId new id', { newId, shortId })
                followId(navigate, newId, shortId)
              }
            }
            frame.DEBUG('Save:SAVED callback', { value })
            good && good(CHG.SAVED, value)
          }
        })
      }
    },
    [
      dispatch,
      changer,
      shortId,
      id,
      frame,
      user,
      navigate,
      actions,
      change,
      notify
    ]
  )

  useEffect(() => {
    dispatch({ type: actions.SAVER, onSave })
  }, [onSave, dispatch, actions.SAVER])

  return onSave
}

function getShorterId(what) {
  return what.shortId || what.handle || what.id
}

////////////////////////////////////////////////////////////////////////////////
// separate Outlet to avoid some inception problems with frame
export function Editable({ routeId, frame, Outlet = ReactOutlet, ...args }) {
  if (!frame.Provider) return <Loader frame={frame} />

  return (
    <frame.Provider routeId={routeId} {...args}>
      {frame.load?.query ? (
        <Loader frame={frame} Outlet={Outlet} />
      ) : frame.change?.mutation ? (
        <Saver frame={frame} Outlet={Outlet} />
      ) : frame.Outlet ? (
        <frame.Outlet />
      ) : (
        <Outlet />
      )}
    </frame.Provider>
  )
}

export function NavEditable({ routeId, sectionId, frame }) {
  useNav(routeId, sectionId)
  return <Editable routeId={routeId} frame={frame} />
}

export function Loader({ frame, Outlet = ReactOutlet }) {
  const { state, skip } = useLoader(frame)

  if (!skip && frame.load.wait && !state._loaded) {
    frame.DEBUG('Loader Loading...')
    return <Loading />
  }

  if (frame.change?.mutation) return <Saver frame={frame} Outlet={Outlet} />
  Outlet = frame.Outlet || Outlet

  return <Outlet />
}

export function Saver({ frame, Outlet = ReactOutlet }) {
  const [{ onSave }] = useContext(frame.Context)

  useSaver(frame)

  if (!onSave) {
    frame.DEBUG(
      'Saver waiting for onSave to appear in state (if it does not, check the state action updates)',
      { onSave }
    )
    return <Loading />
  }

  Outlet = frame.Outlet || Outlet
  return <Outlet />
}
